/**
 * Discrete lab results wire (PROD-2641, Scrips.Patient #997; read side of PROD-2643).
 *
 * A lab request (PatientLabRequest) carries its ordered tests (PatientLabRequestTest) and
 * any number of versioned lab reports (LabReport, FHIR DiagnosticReport), each with its
 * analytes (LabObservation, FHIR Observation, keyed by LOINC or a local code).
 *
 * The JSON is camelCase. Every field is nullable here because the backend ships `null` for
 * unset values. Until the result-table scripts run (dev today) both reads answer 200 with
 * `storeAvailable: false` and empty arrays.
 */

/** PatientLabRequestTest.Status (Domain/Constants/LabRequestTestStatus.cs). */
export const LAB_REQUEST_TEST_STATUS = {
  Ordered: 'Ordered',
  Collected: 'Collected',
  InLab: 'InLab',
  Resulted: 'Resulted',
  NotPerformed: 'NotPerformed',
  Cancelled: 'Cancelled',
} as const;

/** Observation interpretation codes (FHIR v3-ObservationInterpretation subset). */
export type LabInterpretation = 'N' | 'H' | 'L' | 'HH' | 'LL' | 'A' | 'AA';

/** Where a report came from. */
export type LabReportSource = 'Transcribed' | 'Interface' | 'Extracted';

export interface LabRequestTestSummary {
  id?: string | null;
  testCode?: string | null;
  testSystem?: string | null;
  testDisplay?: string | null;
  /** Ordered | Collected | InLab | Resulted | NotPerformed | Cancelled */
  status?: string | null;
  isOmitted?: boolean | null;
}

export interface LabObservation {
  id?: string | null;
  /** The ordered test this analyte answers; null for an analyte not on the order. */
  patientLabRequestTestId?: string | null;
  loinc?: string | null;
  localCode?: string | null;
  localSystem?: string | null;
  /** The Scrips catalogue code and system (PROD-3204); the trend key when there is no LOINC. */
  scripsComponentCode?: string | null;
  scripsComponentSystem?: string | null;
  display?: string | null;
  valueNumeric?: number | null;
  /** `<`, `<=`, `>` or `>=` when the lab printed a bound instead of a value. */
  valueComparator?: string | null;
  valueText?: string | null;
  /**
   * The value exactly as the lab printed it, comparator included ("<0.5", "5,9", "13.20"); set on rows
   * committed from Document Intake, null on others (PROD-3261). For display only: never parsed.
   */
  valuePrinted?: string | null;
  valueCodedCode?: string | null;
  valueCodedDisplay?: string | null;
  /** UCUM unit. */
  unit?: string | null;
  /** The unit as printed, when it is not a UCUM code. */
  unitText?: string | null;
  refLow?: number | null;
  refHigh?: number | null;
  refText?: string | null;
  /**
   * The range regime the lab printed the range under ("Fasting", "Luteal phase"), as Document Intake read
   * it; null when none was printed or the row was not committed from intake (Scrips.Patient #1133).
   */
  rangeRegime?: string | null;
  interpretation?: string | null;
  /** The flag as the laboratory printed it (an uploaded report); interpretation stays null then. */
  interpretationText?: string | null;
  /** preliminary | final | amended | corrected | cancelled | entered-in-error */
  status?: string | null;
  effectiveAt?: string | null;
  /** Specimen | Issued | Unknown; null on a row written before PROD-3204. */
  effectiveAtBasis?: string | null;
  /** Day | Minute | Month: how precise effectiveAt is; null when unknown (PROD-3261). */
  effectiveAtPrecision?: string | null;
  /** Reported | Computed; null when the observation carries no flag. */
  interpretationSource?: string | null;
  source?: string | null;
  isCurrent?: boolean | null;
}

export interface LabReport {
  id?: string | null;
  rootLabReportId?: string | null;
  version?: number | null;
  isCurrent?: boolean | null;
  supersededByLabReportId?: string | null;
  /** Null for an External (outside) report. */
  patientLabRequestId?: string | null;
  /** Ordered | External (PROD-3204). */
  origin?: string | null;
  /** The lab as printed on the document; never inferred (PROD-3204). */
  performingLabName?: string | null;
  labVendorName?: string | null;
  issuedAt?: string | null;
  /** registered | partial | final | amended | corrected | cancelled | entered-in-error */
  status?: string | null;
  /** The attached PDF (a PatientDocument of the same patient). */
  patientDocumentId?: string | null;
  conclusion?: string | null;
  source?: string | null;
  /** Extracted reports: the extraction run, who confirmed the rows (a user id) and when (UTC). */
  extractionRunId?: string | null;
  confirmedBy?: string | null;
  confirmedAt?: string | null;
  attestationVersion?: string | null;
  /** Who wrote this version (a user id) and when (UTC). There is no amend-reason field. */
  createdBy?: string | null;
  createdDate?: string | null;
  observations?: LabObservation[] | null;
}

/** `GET api/Patients/{patientId}/LabRequests/{labRequestId}/Reports`. */
export interface LabRequestReports {
  patientLabRequestId?: string | null;
  patientId?: string | null;
  encounterId?: string | null;
  storeAvailable?: boolean | null;
  testsAvailable?: boolean | null;
  resultedCount?: number | null;
  totalCount?: number | null;
  notPerformedCount?: number | null;
  tests?: LabRequestTestSummary[] | null;
  reports?: LabReport[] | null;
}

/** `GET api/Patients/{patientId}/LabRequests/Reports?labRequestId=…&includeHistory=` (Scrips.Patient #1226). */
export interface PatientLabRequestReports {
  patientId?: string | null;
  /** One entry per requested lab request of this patient, in the order asked. */
  labRequests?: LabRequestReports[] | null;
  /** Requested ids that are not lab requests of this patient in the caller's organization. */
  notFoundLabRequestIds?: string[] | null;
}

/** `GET api/Patients/{patientId}/LabReports?includeHistory=` (PROD-3204): outside reports, no lab request. */
export interface PatientExternalLabReports {
  patientId?: string | null;
  storeAvailable?: boolean | null;
  reports?: LabReport[] | null;
}

/**
 * One point of `GET api/Patients/{patientId}/LabObservations`. The server's LabObservationTrendPointDto
 * extends LabObservationDto and fills it with the same mapper, so `rangeRegime` (#1133) comes with each point.
 */
export interface LabObservationTrendPoint extends LabObservation {
  patientLabRequestId?: string | null;
  rootLabReportId?: string | null;
  reportStatus?: string | null;
  issuedAt?: string | null;
}

/** `GET api/Patients/{patientId}/LabObservations?loinc=` (oldest first by effectiveAt). */
export interface LabObservationTrend {
  patientId?: string | null;
  loinc?: string | null;
  localCode?: string | null;
  localSystem?: string | null;
  scripsComponentCode?: string | null;
  scripsComponentSystem?: string | null;
  storeAvailable?: boolean | null;
  observations?: LabObservationTrendPoint[] | null;
}
